/**
 * Members Only Flyout Handler
 * Manages mouseover/mouseout behavior for the Members Only navigation flyout
 */

class MembersOnlyFlyout {
  constructor(triggerId = 'membersOnlyTrigger', flyoutId = 'membersOnlyFlyout') {
    this.trigger = document.getElementById(triggerId);
    this.flyout = document.getElementById(flyoutId);
    this.backdrop = document.querySelector('.header__members-only-backdrop');
    this.timeoutId = null;
    this.hoverDelay = 150; // ms to wait before showing/hiding

    if (this.trigger && this.flyout) {
      this.init();
    }
  }

  hydrateImages(container = document) {
    const lazyImages = container.querySelectorAll('img[data-members-only-lazy-image][data-src]');

    lazyImages.forEach((image) => {
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
    });
  }

  init() {
    // Trigger events
    this.trigger.addEventListener('mouseenter', () => this.show());
    this.trigger.addEventListener('mouseleave', () => this.hide());
    this.trigger.addEventListener('focus', () => this.show());
    this.trigger.addEventListener('blur', (e) => {
      // Only hide if focus is leaving the entire flyout area
      if (!this.flyout.contains(e.relatedTarget)) {
        this.hide();
      }
    });

    // Flyout events
    this.flyout.addEventListener('mouseenter', () => this.show());
    this.flyout.addEventListener('mouseleave', () => this.hide());

    // Keyboard navigation
    this.setupKeyboardNavigation();

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (
        !this.trigger.contains(e.target) &&
        !this.flyout.contains(e.target)
      ) {
        this.hide();
      }
    });
  }

  show() {
    clearTimeout(this.timeoutId);
    this.hydrateImages(this.flyout);
    this.flyout.classList.add('active');
    if (this.backdrop) this.backdrop.classList.add('active');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  hide() {
    clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      this.flyout.classList.remove('active');
      if (this.backdrop) this.backdrop.classList.remove('active');
      this.trigger.setAttribute('aria-expanded', 'false');
    }, this.hoverDelay);
  }

  setupKeyboardNavigation() {
    this.trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggle();
      } else if (e.key === 'Escape') {
        this.hide();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.focusFirstButton();
      }
    });

    // Focus trap within flyout
    const buttons = this.flyout.querySelectorAll('a, button');
    if (buttons.length > 0) {
      buttons.forEach((button, index) => {
        button.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.hide();
            this.trigger.focus();
          } else if (e.key === 'ArrowUp' && index === 0) {
            e.preventDefault();
            this.trigger.focus();
          } else if (e.key === 'ArrowDown' && index === buttons.length - 1) {
            e.preventDefault();
            buttons[0].focus();
          }
        });
      });
    }
  }

  focusFirstButton() {
    const firstButton = this.flyout.querySelector('a, button');
    if (firstButton) {
      firstButton.focus();
    }
  }

  toggle() {
    if (this.flyout.classList.contains('active')) {
      this.hide();
    } else {
      this.show();
    }
  }
}

function initializeMembersOnlyMobileImageHydration() {
  const membersOnlyMobileTabTrigger = document.querySelector(
    '[data-target="mobile-drawer-tabs-members-only"]',
  );
  const membersOnlyMobilePanel = document.getElementById('mobile-drawer-tabs-members-only');

  if (!membersOnlyMobileTabTrigger || !membersOnlyMobilePanel) return;

  const hydratePanelImages = () => {
    const lazyImages = membersOnlyMobilePanel.querySelectorAll(
      'img[data-members-only-lazy-image][data-src]',
    );

    lazyImages.forEach((image) => {
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
    });
  };

  membersOnlyMobileTabTrigger.addEventListener('click', hydratePanelImages, { once: true });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MembersOnlyFlyout('membersOnlyTrigger', 'membersOnlyFlyout');
    initializeMembersOnlyMobileImageHydration();
  });
} else {
  new MembersOnlyFlyout('membersOnlyTrigger', 'membersOnlyFlyout');
  initializeMembersOnlyMobileImageHydration();
}
